import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  parse,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from "date-fns";
import { RouteProp, useRoute } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { ConfirmDialog } from "../../../shared/ui/ConfirmDialog";
import { Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { useAuth } from "../../../../context/AuthContext";
import { TimeBasedService } from "../../../services/TimeBasedService";
import { AuthStackParamList } from "../../../navigation/navigationTypes";
import { resolveHistoryRange } from "../../../shared/utils/historyNavigation";
import { getMinutesValidationError, parseValidMinutes } from "../../../shared/utils/minutesValidation";

type FilterType = "Weekly" | "Monthly" | "Quarterly" | "Yearly" | "Custom";

type EnumItem = {
  enumerationId: string;
  value: string;
  parameter?: string | number | null;
};

type HistoryRow = {
  date: string;
  data: {
    worshipId: string;
    minutes: any;
    marks: number;
    isEditable: boolean;
  };
};
type PranayamHistoryRouteProp = RouteProp<AuthStackParamList, "PranayamHistory">;

const timeOnlyToMinutes = (value: any) => {
  if (!value) return 0;

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const parts = value.split(":");
    const h = parseInt(parts[0] || "0", 10);
    const m = parseInt(parts[1] || "0", 10);
    return h * 60 + m;
  }

  return 0;
};

const minutesToTimeOnly = (mins: number) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;

  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
};

export function PranayamHistoryScreen() {
  const route = useRoute<PranayamHistoryRouteProp>();
  const { user } = useAuth();
  const today = new Date();
  const initialRange = resolveHistoryRange(route.params, today);

  const effectiveUserId = useMemo(() => {
    return initialRange.devoteeId || user?.userId || "";
  }, [initialRange.devoteeId, user?.userId]);

  const [filterType, setFilterType] = useState<FilterType>(
    initialRange.hasNavigationDates ? "Custom" : "Monthly"
  );
  const [fromDate, setFromDate] = useState(initialRange.fromDate);
  const [toDate, setToDate] = useState(initialRange.toDate);

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [entries, setEntries] = useState<HistoryRow[]>([]);
  const [summary, setSummary] = useState({ isSelf: true });

  const [parameter, setParameter] = useState(0.3333);
  const [worshipTypeId, setWorshipTypeId] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDateLabel, setEditingDateLabel] = useState("");
  const [editMinutes, setEditMinutes] = useState("0");
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const editMinutesError = useMemo(
    () => getMinutesValidationError(editMinutes, 3),
    [editMinutes]
  );

  const calcMarks = (mins: number) => {
    const minutesPerMark = parameter > 0 ? Math.round(1 / parameter) : 3;
    if (mins < minutesPerMark) {
      return 0;
    }

    return Math.floor(mins / minutesPerMark);
  };

  const minutesPerMarkText = useMemo(() => {
    const m = Math.round(1 / parameter);
    return `${m} Minutes = 1 Mark`;
  }, [parameter]);

  /* ---------------- ENUM LOAD ---------------- */

  const loadEnumeration = async () => {
    const res = await TimeBasedService.getEnumeration("WorshipType");

    const list = res?.data as EnumItem[];

    const pranayam = list.find(
      (x) => (x.value || "").trim().toLowerCase() === "pranayam"
    );

    if (!pranayam) {
      Flash.error("Pranayam type not found");
      return;
    }

    setWorshipTypeId(pranayam.enumerationId);

    const p = Number(pranayam.parameter);
    if (p > 0) setParameter(p);
  };

  useEffect(() => {
    loadEnumeration();
  }, []);

  /* ---------------- FILTER ---------------- */

  const applyFilter = (type: FilterType) => {
    setFilterType(type);

    const now = new Date();

    let start = startOfMonth(now);
    let end = endOfMonth(now);

    if (type === "Weekly") {
      start = startOfWeek(now);
      end = endOfWeek(now);
    }

    if (type === "Quarterly") {
      start = startOfQuarter(now);
      end = endOfQuarter(now);
    }

    if (type === "Yearly") {
      start = startOfYear(now);
      end = endOfYear(now);
    }

    setFromDate(format(start, "yyyy-MM-dd"));
    setToDate(format(end, "yyyy-MM-dd"));
  };

  const openPicker = (mode: "from" | "to") => {
    const value = new Date(mode === "from" ? fromDate : toDate);

    DateTimePickerAndroid.open({
      value,
      mode: "date",
      onChange: (event, selectedDate) => {
        if (event.type !== "set" || !selectedDate) return;

        const v = format(selectedDate, "yyyy-MM-dd");

        if (mode === "from") setFromDate(v);
        else setToDate(v);

        setFilterType("Custom");
      },
    });
  };

  /* ---------------- LOAD HISTORY ---------------- */

  const loadHistory = useCallback(async () => {
    if (!user?.userId || !worshipTypeId) return;
    if (!effectiveUserId) return;

    setLoading(true);

    const res = await TimeBasedService.getWorshipHistory({
      userId: effectiveUserId,
      fromDate,
      toDate,
      worshipType: worshipTypeId,
    });

    if (res?.status !== 200) {
      Flash.error("Failed to load history");
      setEntries([]);
      setLoading(false);
      return;
    }
    setSummary(res.data.response);
    const list = res?.data.records || [];

    const normalized = list.map((x: any) => ({
      ...x,
      data: {
        ...x.data,
        minutes: timeOnlyToMinutes(x.data.minutes),
      },
    }));

    setEntries(normalized);

    setLoading(false);
  }, [fromDate, toDate, worshipTypeId, user?.userId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadHistory();
    } finally {
      setRefreshing(false);
    }
  };

  /* ---------------- DELETE ---------------- */

  const confirmDelete = (id: string) => {
    setPendingDeleteId(id);
  };

  /* ---------------- EDIT ---------------- */

  const openEdit = async (id: string, dateLabel: string) => {
    const res = await TimeBasedService.getWorship(id);

    const first = res?.data?.[0];

    if (!first) {
      Flash.error("Entry not found");
      return;
    }

    setEditingId(first.worshipId);
    setEditingDateLabel(dateLabel);

    const mins = timeOnlyToMinutes(first.minutes);
    setEditMinutes(String(mins));
  };

  const saveEdit = async () => {
    if (!editingId) return;

    if (editMinutesError) {
      Flash.error(editMinutesError);
      return;
    }
    const mins = parseValidMinutes(editMinutes) ?? 0;

    const parsed = parse(editingDateLabel, "dd-MM-yyyy", new Date());

    const payload = {
      worshipId: editingId,
      userId: user?.userId,
      date: format(parsed, "yyyy-MM-dd"),
      worshipType: worshipTypeId,
      minutes: minutesToTimeOnly(mins),
      marks: calcMarks(mins),
    };

    const res = await TimeBasedService.create(payload);

    if (res?.status !== 200) {
      Flash.error("Update failed");
      return;
    }

    Flash.success("Success!", "Pranayam Updated Successfully!");

    setEditingId(null);
    loadHistory();
  };

  /* ---------------- UI ---------------- */

  const Chip = ({ text }: { text: FilterType }) => (
    <Pressable
      onPress={() => applyFilter(text)}
      style={[
        styles.chip,
        filterType === text ? styles.chipActive : styles.chipInactive,
      ]}
    >
      <Text style={{ color: filterType === text ? "#fff" : "#B45309", fontWeight: "800" }}>
        {text}
      </Text>
    </Pressable>
  );

  return (
    <DivineBackground overlayOpacity={0.95}>
      <Header title="History & Analytics" showBack showHome />

      <ScrollView
        contentContainerStyle={styles.page}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={["#F97316"]}
            tintColor="#F97316"
          />
        }
      >
        {/* FILTER CARD */}

        <View style={styles.filterCard}>
          {/* <Text>🔎</Text> */}
          <Text style={styles.filterTitle}> 🔎 Filter History</Text>

          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.chipRow}
          >
            <Chip text="Weekly" />
            <Chip text="Monthly" />
            <Chip text="Quarterly" />
            <Chip text="Yearly" />
          </ScrollView>

          <View style={styles.dateRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.dateLabel}>From Date</Text>

              <Pressable
                onPress={() => openPicker("from")}
                style={styles.dateBox}
              >
                <Text style={styles.dateIcon}>📅</Text>
                <Text style={styles.dateText}>
                  {format(new Date(fromDate), "dd-MM-yyyy")}
                </Text>
              </Pressable>
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.dateLabel}>To Date</Text>

              <Pressable
                onPress={() => openPicker("to")}
                style={styles.dateBox}
              >
                <Text style={styles.dateIcon}>📅</Text>
                <Text style={styles.dateText}>
                  {format(new Date(toDate), "dd-MM-yyyy")}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>

        {/* LIST */}

        {loading ? (
          <ActivityIndicator />
        ) : (
          entries.map((row, idx) => {
            const d = row.data;

            return (
              <View key={idx} style={styles.itemCard}>
                <View>
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                    {/* <Text>📅</Text> */}
                    <Text style={styles.itemDate}>{row.date}</Text>
                  </View>

                  <Text style={styles.itemDay}>
                    {format(parse(row.date, "dd-MM-yyyy", new Date()), "EEEE")}
                  </Text>
                </View>

                <View style={styles.itemRight}>
                  <View>
                    <Text style={styles.itemValue}>{d.minutes}</Text>
                    <Text style={styles.small}>Minutes</Text>
                  </View>

                  <View>
                    <Text style={styles.itemValue}>{d.marks}</Text>
                    <Text style={styles.small}>Marks</Text>
                  </View>

                  {summary.isSelf && (
                    <View style={styles.actions}>
                      {d.isEditable ? (
                        <Pressable onPress={() => openEdit(d.worshipId, row.date)} style={styles.iconBtn} disabled={loading}>
                          <Text>✏️</Text>
                        </Pressable>
                      ) : (
                        <View style={[styles.iconBtn, styles.iconBtnPlaceholder]} />
                      )}

                      <Pressable
                        onPress={() => confirmDelete(d.worshipId)}
                        style={[styles.iconBtn, styles.iconBtnDanger]}
                        disabled={loading}
                      >
                        <Text>🗑️</Text>
                      </Pressable>
                    </View>
                  )}

                </View>
              </View>
            );
          })
        )}
      </ScrollView>

      {/* EDIT MODAL */}

      <Modal
        transparent
        visible={!!editingId}
        animationType="fade"
        onRequestClose={() => {
          if (!loading) setEditingId(null);
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Edit Entry</Text>

              <Pressable onPress={() => setEditingId(null)} disabled={loading}>
                <Text style={styles.modalClose}>✕</Text>
              </Pressable>
            </View>

            <Text style={{ marginBottom: 10 }}>Date: {editingDateLabel}</Text>

            <Input
              label="Number of Minutes"
              keyboardType="number-pad"
              value={editMinutes}
              onChangeText={(t) => setEditMinutes(t.replace(/[^\d]/g, ""))}
            />
            {editMinutesError ? <Text style={styles.validationText}>{editMinutesError}</Text> : null}

            <Text style={{ marginTop: 8 }}>{minutesPerMarkText}</Text>

            <View style={styles.modalBtns}>
              <Pressable
                style={styles.cancelBtn}
                onPress={() => setEditingId(null)}
                disabled={loading}
              >
                <Text style={styles.cancelText}>Cancel</Text>
              </Pressable>

              <Pressable
                style={[styles.updateBtn, loading && styles.buttonDisabled]}
                onPress={saveEdit}
                disabled={loading || !!editMinutesError}
              >
                <Text style={styles.updateText}>{loading ? "Updating..." : "Update"}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <ConfirmDialog
        visible={!!pendingDeleteId}
        title="Delete Entry"
        message="Are you sure?"
        confirmText="Delete"
        destructive
        loading={loading}
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={async () => {
          if (!pendingDeleteId) return;
          try {
            setLoading(true);
            await TimeBasedService.delete(pendingDeleteId);
            Flash.success("Success!", "Pranayam Deleted Successfully!");
            setPendingDeleteId(null);
            loadHistory();
          } finally {
            setLoading(false);
          }
        }}
      />
    </DivineBackground>
  );
}

const styles = StyleSheet.create({
  page: { padding: 14, gap: 12 },

  filterCard: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 14,
  },

  filterTitle: {
    fontWeight: "900",
    marginBottom: 10,
  },

  chipRow: {
    flexDirection: "row",
    gap: 8,
    paddingBottom: 4,
  },

  chip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
  },
  actions: { flexDirection: "row", gap: 10 },
  iconBtn: {
    minWidth: 45,
    height: 34,
    paddingHorizontal: 10,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(234,88,12,0.10)",
  },
  iconBtnDanger: {
    backgroundColor: "rgba(220,38,38,0.10)",
  },
  iconBtnPlaceholder: {
    opacity: 0,
  },
  chipActive: {
    backgroundColor: "#EA580C",
  },

  chipInactive: {
    backgroundColor: "#FED7AA",
  },

  dateRow: {
    flexDirection: "row",
    gap: 10,
  },

  dateLabel: {
    fontSize: 12,
    marginBottom: 4,
    color: "#555",
  },

  dateBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 10,
    borderWidth: 1,
    borderRadius: 10,
    borderColor: "#ddd",
    backgroundColor: "#F9FAFB",
  },

  dateIcon: {
    fontSize: 14,
  },

  dateText: {
    fontSize: 13,
    fontWeight: "700",
  },

  itemCard: {
    backgroundColor: "#fff",
    borderRadius: 14,
    padding: 14,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  itemDay: {
    fontSize: 11,
    color: "#777",
    marginTop: 2,
  },

  itemRight: {
    flexDirection: "row",
    gap: 14,
    alignItems: "center",
  },

  itemDate: {
    fontWeight: "900",
  },

  itemValue: {
    fontWeight: "900",
    color: "#EA580C",
  },

  small: {
    fontSize: 10,
    color: "#777",
  },

  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.35)",
    justifyContent: "center",
    padding: 16,
  },

  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },

  modalClose: {
    fontSize: 18,
    fontWeight: "900",
    color: "#555",
  },

  modalCard: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 16,
  },

  modalTitle: {
    fontWeight: "900",
    fontSize: 16,
    marginBottom: 10,
  },
  validationText: {
    marginTop: 6,
    fontSize: 11,
    fontWeight: "800",
    color: "#DC2626",
  },

  modalBtns: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    marginTop: 20,
  },

  cancelBtn: {
    flex: 1,
    height: 46,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#EA580C",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#fff",
  },

  cancelText: {
    color: "#EA580C",
    fontWeight: "800",
  },

  updateBtn: {
    flex: 1,
    height: 46,
    borderRadius: 10,
    backgroundColor: "#EA580C",
    justifyContent: "center",
    alignItems: "center",
  },

  updateText: {
    color: "#fff",
    fontWeight: "800",
  },
  buttonDisabled: {
    opacity: 0.6,
  },
});
